/**
 * useTabLeader — REUSABLE CROSS-TAB LEADER ELECTION (+ BroadcastChannel)
 *
 * Generalises the pattern proven in features/tasks/hooks/useNotificationSync.ts:
 * across all browser tabs of the same origin, exactly ONE tab (the "leader")
 * talks to the backend; it broadcasts the result to the other tabs, which apply
 * it locally with zero network traffic.
 *
 *   100 users × 10 tabs polling a badge endpoint → 1000 requests/interval.
 *   With this hook → 100 requests/interval (one per browser).
 *
 * The concern (e.g. nav badges) supplies two callbacks:
 *   - onLeaderPoll: runs ONLY on the leader, on each poll tick (and on demand).
 *     Fetch here and call ctx.broadcast(payload) to push the result to peers.
 *   - onMessage: runs on EVERY tab when a peer broadcasts a payload. Apply it to
 *     the local cache here.
 *
 * The "give me data now" round-trip (a freshly opened follower with no data yet)
 * is built in: call the returned `requestRefresh()` — on the leader it polls
 * immediately, on a follower it asks the leader to poll and broadcast.
 *
 * Fallback: when BroadcastChannel is unavailable (old Safari, some private modes)
 * every tab simply polls on its own — identical to having no coordination.
 *
 * Leader election mirrors useNotificationSync: a localStorage heartbeat acts as a
 * shared lease, claimed with jitter + double-check to avoid two tabs racing, and
 * a watchdog re-claims it if the leader dies (crash, OOM, force-close) without
 * firing beforeunload. See that file for the detailed rationale.
 */

import { useEffect, useRef } from "react";

export interface TabLeaderCtx {
  /** Is THIS tab currently the leader? */
  isLeader: boolean;
  /** Send a payload to every other tab (delivered to their onMessage). */
  broadcast: (payload: unknown) => void;
}

export interface TabLeaderHandlers {
  /** Leader-only. Called on each poll tick and on demand — fetch + broadcast here. */
  onLeaderPoll: (ctx: TabLeaderCtx) => void | Promise<void>;
  /** Every tab. Called when a peer broadcasts a payload — apply it to cache here. */
  onMessage?: (payload: unknown, ctx: TabLeaderCtx) => void;
}

export interface TabLeaderConfig {
  /** BroadcastChannel name — must be unique per concern, identical across tabs. */
  channelName: string;
  /** localStorage key holding the leader heartbeat timestamp — unique per concern. */
  storageKey: string;
  /** When false the hook is inert (e.g. logged-out). Default true. */
  enabled?: boolean;
  /** How often the leader polls. Default 60s. */
  pollMs?: number;
  /** How stale a heartbeat may be before the leader is presumed dead. Default 35s. */
  leaderTtlMs?: number;
  /** How often the leader refreshes its heartbeat. Must be < leaderTtlMs. Default 10s. */
  heartbeatMs?: number;
  /** Pause polling after this much user inactivity. Default 5 min. */
  idleThresholdMs?: number;
  /**
   * When true, becoming leader does NOT immediately poll (a mount-time query is
   * expected to do the first fetch — useNotificationSync's situation). Default
   * false: a fresh leader fetches right away.
   */
  skipInitialPoll?: boolean;
}

export interface TabLeaderApi {
  /** Trigger a poll now: leader fetches+broadcasts; follower asks the leader to. */
  requestRefresh: () => void;
}

type Wrapped =
  | { __tl: "data"; payload: unknown }
  | { __tl: "refresh" };

export function useTabLeader(
  config: TabLeaderConfig,
  handlers: TabLeaderHandlers,
): TabLeaderApi {
  const {
    channelName,
    storageKey,
    enabled = true,
    pollMs = 60_000,
    leaderTtlMs = 35_000,
    heartbeatMs = 10_000,
    idleThresholdMs = 5 * 60_000,
    skipInitialPoll = false,
  } = config;

  // Keep handlers in a ref so the effect (set up once) always calls the latest.
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  const isLeader = useRef(false);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActiveAt = useRef(Date.now());
  const wasIdle = useRef(false);

  // Stable API: `requestRefresh` keeps the same identity across renders and
  // delegates to the live implementation set up inside the effect below. (We must
  // NOT hand back a function the effect reassigns by property — the caller would
  // snapshot the initial no-op, since mutating a ref triggers no re-render.)
  const implRef = useRef<() => void>(() => {});
  const apiRef = useRef<TabLeaderApi>({ requestRefresh: () => implRef.current() });

  useEffect(() => {
    if (!enabled) return;

    const ctx = (): TabLeaderCtx => ({
      isLeader: isLeader.current,
      broadcast: (payload: unknown) =>
        channelRef.current?.postMessage({ __tl: "data", payload } satisfies Wrapped),
    });

    function runLeaderPoll(): void {
      void handlersRef.current.onLeaderPoll(ctx());
    }

    function becomeLeader(): void {
      isLeader.current = true;
      localStorage.setItem(storageKey, String(Date.now()));
      if (!skipInitialPoll) runLeaderPoll();

      pollTimerRef.current = setInterval(() => {
        if (Date.now() - lastActiveAt.current > idleThresholdMs) {
          wasIdle.current = true;
          return;
        }
        runLeaderPoll();
      }, pollMs);

      heartbeatTimerRef.current = setInterval(() => {
        localStorage.setItem(storageKey, String(Date.now()));
      }, heartbeatMs);
    }

    function resignLeader(): void {
      if (!isLeader.current) return;
      isLeader.current = false;
      // Let the heartbeat expire naturally rather than deleting it — deleting on a
      // React StrictMode unmount→remount cycle would make this tab re-claim and
      // double-fetch. TTL + watchdog handle real death.
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    }

    async function tryClaimLeader(): Promise<void> {
      if (isLeader.current) return;
      const ts = Number(localStorage.getItem(storageKey) ?? 0);
      if (Date.now() - ts < leaderTtlMs) return; // a leader is alive

      // Jitter + double-check so two tabs don't both claim on the same death.
      await new Promise<void>((resolve) => setTimeout(resolve, Math.random() * 300));
      if (isLeader.current) return;
      const ts2 = Number(localStorage.getItem(storageKey) ?? 0);
      if (Date.now() - ts2 < leaderTtlMs) return; // someone else won the race

      becomeLeader();
    }

    function markActive(): void {
      const wasPreviouslyIdle = wasIdle.current;
      lastActiveAt.current = Date.now();
      if (!wasPreviouslyIdle) return;
      wasIdle.current = false;
      // Coming back from idle — refresh immediately instead of waiting a full tick.
      if (isLeader.current) runLeaderPoll();
      else channelRef.current?.postMessage({ __tl: "refresh" } satisfies Wrapped);
    }

    const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"] as const;
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActive, { passive: true }));
    const onVisibility = () => {
      if (document.visibilityState === "visible") markActive();
    };
    document.addEventListener("visibilitychange", onVisibility);

    // ── Fallback: no BroadcastChannel → every tab polls itself ────────────────
    if (!("BroadcastChannel" in window)) {
      runLeaderPoll();
      pollTimerRef.current = setInterval(() => {
        if (Date.now() - lastActiveAt.current > idleThresholdMs) {
          wasIdle.current = true;
          return;
        }
        runLeaderPoll();
      }, pollMs);
      implRef.current = runLeaderPoll;
      return () => {
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        implRef.current = () => {};
        ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActive));
        document.removeEventListener("visibilitychange", onVisibility);
      };
    }

    // ── Normal flow ──────────────────────────────────────────────────────────
    const bc = new BroadcastChannel(channelName);
    channelRef.current = bc;

    bc.onmessage = (event: MessageEvent<Wrapped>) => {
      const msg = event.data;
      if (msg?.__tl === "data") {
        handlersRef.current.onMessage?.(msg.payload, ctx());
      } else if (msg?.__tl === "refresh" && isLeader.current) {
        runLeaderPoll();
      }
    };

    implRef.current = () => {
      if (isLeader.current) runLeaderPoll();
      else bc.postMessage({ __tl: "refresh" } satisfies Wrapped);
    };

    void tryClaimLeader();
    const watchdog = setInterval(() => {
      void tryClaimLeader();
    }, leaderTtlMs);
    window.addEventListener("beforeunload", resignLeader);

    return () => {
      resignLeader();
      clearInterval(watchdog);
      bc.close();
      channelRef.current = null;
      implRef.current = () => {};
      window.removeEventListener("beforeunload", resignLeader);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActive));
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // Re-run only when the concern's identity or enablement changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, channelName, storageKey, pollMs, leaderTtlMs, heartbeatMs, idleThresholdMs, skipInitialPoll]);

  return apiRef.current;
}
